import React from 'react';
import { Box, Typography, TextField, Stack, IconButton } from '@mui/material';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import ClearIcon from '@mui/icons-material/Clear';
import { GT_COLORS } from '../../../../components/groups-teams/shared/constants';

interface Contact {
    full_name: string;
    phone_number: string;
}

interface DefaultContactsCardProps {
    contacts: Contact[];
    onChange: (contacts: Contact[]) => void;
    onSave: () => void;
    onClear: (contacts: Contact[]) => void;
    phoneErrors?: string[];
    nameErrors?: string[];
    /** True when the resolved effective-settings fetch failed — the on-screen
     * contacts aren't confirmed current, so editing/saving is disabled rather
     * than risking an overwrite of the team's real dispatch contacts. */
    disabled?: boolean;
}

const EMPTY_CONTACT: Contact = { full_name: '', phone_number: '' };

const formatPhone = (raw: string): string => {
    // Ensure +1 prefix, cap at 10 digits after +1
    let v = raw.replace(/[^0-9+]/g, '');
    if (!v.startsWith('+1')) {
        v = '+1' + v.replace(/^\+/, '');
    }
    // +1 plus max 10 digits = 12 characters
    if (v.length > 12) {
        v = v.slice(0, 12);
    }
    return v;
};

export const DefaultContactsCard: React.FC<DefaultContactsCardProps> = ({
    contacts,
    onChange,
    onSave,
    onClear,
    phoneErrors = [],
    nameErrors = [],
    disabled = false,
}) => {
    const safeContacts: Contact[] = [
        contacts[0] || { ...EMPTY_CONTACT },
        contacts[1] || { ...EMPTY_CONTACT },
    ];

    const handleChange = (index: number, field: keyof Contact, value: string) => {
        if (disabled) return;
        const updated = [...safeContacts];
        if (field === 'phone_number') {
            updated[index] = { ...updated[index], [field]: formatPhone(value) };
        } else {
            updated[index] = { ...updated[index], [field]: value };
        }
        onChange(updated);
    };

    const handleClear = (index: number) => {
        if (disabled) return;
        const updated = [...safeContacts];
        updated[index] = { ...EMPTY_CONTACT };
        onChange(updated);
        // Pass the cleared array directly to avoid stale ref race condition
        onClear(updated);
    };

    const hasContent = (c: Contact) => c.full_name.trim() || c.phone_number.replace(/[^0-9]/g, '').length > 1;

    return (
        <Box
            sx={{
                backgroundColor: GT_COLORS.cardBg,
                border: `1px solid ${GT_COLORS.border}`,
                borderRadius: '10px',
                padding: '20px',
                gridColumn: '1 / -1',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '6px' }}>
                <ContactPhoneIcon sx={{ fontSize: '18px', color: GT_COLORS.primary }} />
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>Default Emergency Contacts</Typography>
            </Box>

            <Typography sx={{ fontSize: '13px', color: GT_COLORS.textMuted, mb: '16px' }}>
                Define up to 2 contacts that Dispatch will call when a Check On Me escalation occurs.
                If a calendar schedule is configured, these contacts serve as the fallback for any time blocks without assigned contacts.
            </Typography>

            {disabled && (
                <Typography sx={{ fontSize: '13px', color: GT_COLORS.red, mb: '16px' }}>
                    Couldn't confirm the current contacts. Editing is disabled until this loads — reload the page to try again.
                </Typography>
            )}

            <Box
                sx={{
                    border: `1px solid ${GT_COLORS.border}`,
                    borderRadius: '8px',
                    overflow: 'hidden',
                }}
            >
                {[0, 1].map((i) => (
                    <Box
                        key={i}
                        sx={{
                            padding: '14px 16px',
                            borderBottom: i === 0 ? `1px solid ${GT_COLORS.borderLight}` : 'none',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '8px' }}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: GT_COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '.3px' }}>
                                Contact {i + 1}
                            </Typography>
                            {hasContent(safeContacts[i]) && (
                                <IconButton size="small" disabled={disabled} onClick={() => handleClear(i)} sx={{ color: GT_COLORS.textFaint, '&:hover': { color: GT_COLORS.red } }}>
                                    <ClearIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            )}
                        </Box>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Full Name"
                                size="small"
                                disabled={disabled}
                                value={safeContacts[i].full_name}
                                onChange={(e) => handleChange(i, 'full_name', e.target.value)}
                                onBlur={onSave}
                                error={!!nameErrors[i]}
                                helperText={nameErrors[i]}
                                fullWidth
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '14px' } }}
                            />
                            <TextField
                                label="Phone Number"
                                size="small"
                                disabled={disabled}
                                value={safeContacts[i].phone_number || '+1'}
                                onChange={(e) => handleChange(i, 'phone_number', e.target.value)}
                                onBlur={onSave}
                                error={!!phoneErrors[i]}
                                helperText={phoneErrors[i]}
                                fullWidth
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '14px' } }}
                            />
                        </Stack>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};
